import React, { useState, useEffect, useRef } from 'react';
import { BiSolidDownArrow } from 'react-icons/bi';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isFlagDropdownOpen, setIsFlagDropdownOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en-US');
  const [selectedRegion, setSelectedRegion] = useState('north-america');
  const [rememberSelection, setRememberSelection] = useState(false);
  const [showLoginTooltip, setShowLoginTooltip] = useState(false);
  const flagDropdownRef = useRef(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleDropdown = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const toggleFlagDropdown = () => {
    setIsFlagDropdownOpen(!isFlagDropdownOpen);
  };

  const handleSelect = () => {
    // Handle the selection - you can add logic here to save preferences
    console.log('Selected Region:', selectedRegion);
    console.log('Selected Language:', selectedLanguage);
    console.log('Remember Selection:', rememberSelection);
    setIsFlagDropdownOpen(false);
  };

  const regions = [
    { id: 'north-america', name: 'North America' },
    { id: 'europe', name: 'Europe' },
    { id: 'asia-pacific', name: 'Asia Pacific' },
    { id: 'latin-america', name: 'Latin America' },
    { id: 'middle-east-africa', name: 'Middle East & Africa' },
  ];

  const languages = [
    { code: 'en-US', name: 'United States - English', flag: '🇺🇸', country: 'United States' },
    { code: 'en-GB', name: 'United Kingdom - English', flag: '🇬🇧', country: 'United Kingdom' },
    { code: 'es-ES', name: 'Spain - Español', flag: '🇪🇸', country: 'Spain' },
    { code: 'fr-FR', name: 'France - Français', flag: '🇫🇷', country: 'France' },
    { code: 'de-DE', name: 'Germany - Deutsch', flag: '🇩🇪', country: 'Germany' },
    { code: 'it-IT', name: 'Italy - Italiano', flag: '🇮🇹', country: 'Italy' },
    { code: 'pt-BR', name: 'Brazil - Português', flag: '🇧🇷', country: 'Brazil' },
    { code: 'ja-JP', name: 'Japan - 日本語', flag: '🇯🇵', country: 'Japan' },
    { code: 'ko-KR', name: 'South Korea - 한국어', flag: '🇰🇷', country: 'South Korea' },
    { code: 'zh-CN', name: 'China - 中文', flag: '🇨🇳', country: 'China' },
  ];

  const currentLanguage = languages.find(lang => lang.code === selectedLanguage) || languages[0];
  // const currentRegion = regions.find(region => region.id === selectedRegion) || regions[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (flagDropdownRef.current && !flagDropdownRef.current.contains(event.target)) {
        setIsFlagDropdownOpen(false);
      }
    };

    if (isFlagDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFlagDropdownOpen]);

  // Show login tooltip on initial page load
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoginTooltip(true);
    }, 1000); // Show tooltip after 1 second

    return () => clearTimeout(timer);
  }, []);

  return (
    <header className="bg-white">
      {/* Top utility bar: language selector + search */}
      <div className="bg-white">
        <div className="container mx-auto px-4 max-w-[1216px]">
          <div className="flex items-center justify-end py-2 gap-6">
            {/* Language selector with flags */}
            <div className="relative" ref={flagDropdownRef}>
              <button 
                onClick={toggleFlagDropdown}
                className="flex items-center gap-1 text-sm text-[#333] focus:outline-none"
              >
                <span className="text-lg ">{currentLanguage.flag}</span>
                <span className='hover:underline'>English</span>
                <BiSolidDownArrow className="w-2 h-2 "  color='black'/>
              </button>
              
              {/* Language and Region Selection Dialog */}
              {isFlagDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-[0_5px_5px_-3px_rgba(0,0,0,0.2),0_8px_10px_1px_rgba(0,0,0,0.14),0_3px_14px_2px_rgba(0,0,0,0.12)] z-[200] w-[290px] px-6 py-8">
                  <div className="space-y-2.5">
                    {/* Select Region */}
                    <div className="flex flex-col gap-2 relative">
                      <label className="text-xs leading-6 font-medium text-gray-500">Select Region</label>
                      <div className="relative">
                        <select
                          value={selectedRegion}
                          onChange={(e) => setSelectedRegion(e.target.value)}
                          style={{
                            padding: '12px 36px 12px 16px',
                          }}
                          className="w-full  border border-gray-400 rounded appearance-none text-base leading-6 font-normal hover:bg-[#e0f2ff]  bg-white text-[#006dc0] focus:border-[#0860c4] focus:ring-1 focus:ring-[#0860c4] !important"
                        >
                          {regions.map((region) => (
                            <option key={region.id} value={region.id}>
                              {region.name}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                          <BiSolidDownArrow className="w-3 h-3 text-[#006dc0]" />
                        </div>
                      </div>
                    </div>

                    {/* Select Language */}
                    <div className="flex flex-col gap-2 relative">
                      <label className="text-base leading-6 font-normal text-gray-700">Select Language</label>
                      <div className="relative">
                        <select
                          value={selectedLanguage}
                          onChange={(e) => setSelectedLanguage(e.target.value)}
                          style={{
                            padding: '12px 36px 12px 16px',
                          }}
                          className="w-full  border border-gray-400 rounded appearance-none text-base leading-6 font-normal hover:bg-[#e0f2ff] active:bg-[#e0f2ff] bg-white text-[#006dc0]"
                        >
                          {languages.map((language) => (
                            <option key={language.code} value={language.code}>
                              {language.name}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
                          <BiSolidDownArrow className="w-3 h-3 text-[#006dc0]" />
                        </div>
                      </div>
                    </div>

                    {/* Remember Selection Checkbox */}
                    <button className="flex flex-col gap-2 relative focus:border-[#0860c4] rounded-md p-1 focus:ring-2 focus:ring-[#0860c4]" tabIndex="0">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={rememberSelection}
                          onChange={(e) => setRememberSelection(e.target.checked)}
                          style={{
                            accentColor: '#0a78f5',
                            borderColor: '#0a78f5',
                            borderWidth: '1px',
                            borderStyle: 'solid',
                            borderRadius: '2px',
                            width: '18px',
                            height: '18px',
                            cursor: 'pointer'
                          }}
                        />
                        <span className="ml-3 text-base leading-6 font-normal text-gray-700">Remember this selection?</span>
                      </label>
                    </button>

                    {/* Select Button */}
                    <button
                      onClick={handleSelect}
                      className="w-full h-11 bg-[#0860c4] text-white text-base leading-6 font-normal rounded-sm hover:bg-[#005a9e] focus:outline-none focus:ring-1 focus:ring-[#006dc0] focus:ring-offset-1 transition-colors"
                    >
                      Select
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Search input */}
            <div className="relative w-64">
              <input
                type="text"
                placeholder="Search AA.com®"
                className="w-full h-9 rounded-sm border border-gray-300 pl-3 pr-10 text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#0078d2] focus:border-[#0078d2]"
                aria-label="Search AA.com®"
              />
              <button
                aria-label="Submit search"
                className="absolute right-1 top-1.5 p-1 text-[#0078d2] hover:text-[#0061ab]"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="11" cy="11" r="7" strokeWidth="2"/><path d="M20 20l-3.5-3.5" strokeWidth="2"/></svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="container mx-auto px-4 max-w-[1216px]">
        <div className="flex items-center justify-between py-3">
          {/* Logos */}
          <div className="flex items-center gap-3" style={{
            flex: "0 0 calc(25%)",
            maxWidth: "calc(25%)",
            boxSizing: "border-box"
          }}>
            <a href="/" className="flex items-center">
              <img
                src="/content/images/homepage/hero/en_US/logo-american-short-header.png"
                alt="American Airlines"
                className="w-[210px] h-auto"
              />
            </a>
            <a href="/oneworld" className="hidden md:block">
              <img
                src="https://www.aa.com/content/images/chrome/rebrand/oneworld.png"
                alt="oneworld"
                className="w-10 h-10"
              />
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center justify-center gap-8" style={{
            flex: "0 0 calc(50% )",
            maxWidth: "calc(50% )",
            boxSizing: "border-box"
          }}>
            <div className="relative">
              <button
                onClick={() => toggleDropdown('plan')}
                className={`text-[#181e25] font-semibold hover:text-[#0061ab] focus:outline-none pb-3 ${activeDropdown === 'plan' ? 'border-b-4 border-[#0078d2]' : ''}`}
              >
                <span className="text-[#181e25] font-semibold text-lg">Plan travel</span>
              </button>
              {activeDropdown === 'plan' && (
                <div className="absolute px-6 top-full transform -translate-x-1/3 mt-3 w-screen  bg-white border-t z-50 max-w-[1216px]">
                  {/* Top divider */}
                  <div className="border-b border-gray-200 mx-4"></div>
                  
                  <div className="px-4 py-6">
                    <div className="grid grid-cols-12 gap-6">
                      <div className="col-span-3">
                        <span className="text-2xl text-gray-500"><strong>Taking a trip?</strong><br />We have your<br />travel plans covered.</span>
                      </div>
                      
                      <div className="col-span-3">
                        <div className="space-y-3">
                          <a href="/reservation/roundTripSearchAccess.do" className="block text-[#0078d2] hover:underline text-sm">Book flights</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="https://www.aadvantagehotels.com/" target="_blank" rel="noreferrer" className="block text-[#0078d2] hover:underline text-sm">Hotels</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="https://aadvantagecars.com/" target="_blank" rel="noreferrer" className="block text-[#0078d2] hover:underline text-sm">Cars</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="https://www.aavacations.com/homePage" target="_blank" rel="noreferrer" className="block text-[#0078d2] hover:underline text-sm">Vacations</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="https://www.aadvantagecruises.com/" target="_blank" rel="noreferrer" className="block text-[#0078d2] hover:underline text-sm">Cruises</a>
                          <div className="border-t border-gray-200"></div>
                        </div>
                      </div>
                      
                      <div className="col-span-3">
                        <div className="space-y-3">
                          <a href="https://www.aa.com/en-us/flights" target="_blank" rel="noreferrer" className="block text-[#0078d2] hover:underline text-sm">Today's flight deals</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="/i18n/aadvantage-program/miles/redeem/redeem-miles.jsp" className="block text-[#0078d2] hover:underline text-sm">Book with miles</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="/i18n/customer-service/payment-options/travel-credit.jsp" className="block text-[#0078d2] hover:underline text-sm">Book with travel credit</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="/travelInformation/flights/schedule" className="block text-[#0078d2] hover:underline text-sm">Flight schedules and notifications</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="https://apply.joinsherpa.com/map?affiliateId=americanairlines" target="_blank" rel="noreferrer" className="block text-[#0078d2] hover:underline text-sm">Find the latest travel requirements</a>
                          <div className="border-t border-gray-200"></div>
                        </div>
                      </div>
                      
                      {/* Vertical divider */}
                      <div className="col-span-1 flex justify-center">
                        <div className="w-px bg-gray-200 h-full"></div>
                      </div>
                      
                      <div className="col-span-2">
                        <div className="space-y-2">
                          <a href="/travelInformation/flights/status" className="block text-gray-700 hover:text-[#0078d2] text-sm">Flight status</a>
                          <a href="/reservation/flightCheckInViewReservationsAccess.do" className="block text-gray-700 hover:text-[#0078d2] text-sm">Check in</a>
                          <a href="/reservation/viewReservationsAccess.do" className="block text-gray-700 hover:text-[#0078d2] text-sm">Your trips</a>
                          <a href="/loyalty/profile/summary" className="block text-gray-700 hover:text-[#0078d2] text-sm">Your account</a>
                          <a href="/AAdvantage/quickEnroll.do" className="block text-gray-700 hover:text-[#0078d2] text-sm">Join AAdvantage®</a>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='flex justify-center mb-2'>
                    <button className='bg-white rounded-md px-3 py-2 focus:border-2 focus:border-[#0860c4] focus:ring-1 focus:ring-[#0860c4]' onClick={() => setActiveDropdown(null)}>
                      <BiSolidDownArrow className='w-4 h-4 text-[#333] rotate-180' />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => toggleDropdown('travel')}
                className={`text-[#181e25] font-semibold hover:text-[#0061ab] focus:outline-none pb-3 ${activeDropdown === 'travel' ? 'border-b-4 border-[#0078d2]' : ''}`}
              >
                <span className="text-[#181e25] font-semibold text-lg">Travel information</span>
              </button>
              {activeDropdown === 'travel' && (
                <div className="absolute px-6 top-full transform -translate-x-[43%] mt-3 w-screen  bg-white border-t z-50 max-w-[1216px]">
                  {/* Top divider */}
                  <div className="border-b border-gray-200 mx-4"></div>
                  
                  <div className="px-4 py-6">
                    <div className="grid grid-cols-12 gap-6">
                      <div className="col-span-3">
                        <span className="text-2xl text-gray-500"><strong>Taking a vacation?</strong><br />Allow us to simplify<br />your travels.</span>
                      </div>
                      
                      <div className="col-span-3">
                        <div className="space-y-3">
                          <a href="/i18n/travel-info/at-the-airport.jsp" className="block text-[#0078d2] hover:underline text-sm">At the airport</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="/i18n/travel-info/experience/travel-experience.jsp" className="block text-[#0078d2] hover:underline text-sm">Travel experience</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="/i18n/travel-info/travel-tools/mobile-and-app.jsp" className="block text-[#0078d2] hover:underline text-sm">Mobile and app</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="/i18n/travel-info/experience/planes/planes.jsp" className="block text-[#0078d2] hover:underline text-sm">Planes</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="/i18n/travel-info/clubs/lounges.jsp" className="block text-[#0078d2] hover:underline text-sm">Lounges</a>
                          <div className="border-t border-gray-200"></div>
                        </div>
                      </div>
                      
                      <div className="col-span-3">
                        <div className="space-y-3">
                          <a href="/i18n/travel-info/baggage/baggage.jsp" className="block text-[#0078d2] hover:underline text-sm">Bags</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="/i18n/travel-info/international-travel/international-travel.jsp" className="block text-[#0078d2] hover:underline text-sm">International travel</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="/i18n/travel-info/special-assistance/special-assistance.jsp" className="block text-[#0078d2] hover:underline text-sm">Special assistance</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="/i18n/travel-info/partner-airlines/airline-partnerships.jsp" className="block text-[#0078d2] hover:underline text-sm">Airline partnerships</a>
                          <div className="border-t border-gray-200"></div>
                        </div>
                      </div>
                      
                      {/* Vertical divider */}
                      <div className="col-span-1 flex justify-center">
                        <div className="w-px bg-gray-200 h-full"></div>
                      </div>
                      
                      <div className="col-span-2">
                        <div className="space-y-2">
                          <a href="/travelInformation/flights/status" className="block text-gray-700 hover:text-[#0078d2] text-sm">Flight status</a>
                          <a href="/reservation/flightCheckInViewReservationsAccess.do" className="block text-gray-700 hover:text-[#0078d2] text-sm">Check in</a>
                          <a href="/reservation/viewReservationsAccess.do" className="block text-gray-700 hover:text-[#0078d2] text-sm">Your trips</a>
                          <a href="/loyalty/profile/summary" className="block text-gray-700 hover:text-[#0078d2] text-sm">Your account</a>
                          <a href="/AAdvantage/quickEnroll.do" className="block text-gray-700 hover:text-[#0078d2] text-sm">Join AAdvantage®</a>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='flex justify-center mb-2'>
                    <button className='bg-white rounded-md px-3 py-2 focus:border-2 focus:border-[#0860c4] focus:ring-1 focus:ring-[#0860c4]' onClick={() => setActiveDropdown(null)}>
                      <BiSolidDownArrow className='w-4 h-4 text-[#333] rotate-180' />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="relative">
              <button
                onClick={() => toggleDropdown('advantage')}
                className={`text-[#181e25] font-semibold hover:text-[#0061ab] focus:outline-none pb-3 ${activeDropdown === 'advantage' ? 'border-b-4 border-[#0078d2]' : ''}`}
              >
                <span className="text-[#181e25] font-semibold text-lg">AAdvantage®</span>
              </button>
              {activeDropdown === 'advantage' && (
                <div className="absolute px-6 top-full transform -translate-x-[57.5%] mt-3 w-screen  bg-white border-t z-50 max-w-[1216px]">
                  {/* Top divider */}
                  <div className="border-b border-gray-200 mx-4"></div>
                  
                  <div className="px-4 py-6">
                    <div className="grid grid-cols-12 gap-6">
                      <div className="col-span-3">
                        <span className="text-2xl text-gray-500"><strong>Traveling the world?</strong><br />Our loyalty program<br />can get you there.</span>
                      </div>
                      
                      <div className="col-span-3">
                        <div className="space-y-3">
                          <a href="/i18n/aadvantage-program/overview.html" className="block text-[#0078d2] hover:underline text-sm">AAdvantage® program</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="/i18n/aadvantage-program/discover/benefits-rewards.html" className="block text-[#0078d2] hover:underline text-sm">Benefits and rewards</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="/i18n/aadvantage-program/earn-miles/earn-miles.html" className="block text-[#0078d2] hover:underline text-sm">Earn miles</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="/i18n/aadvantage-program/use-miles/use-miles.html" className="block text-[#0078d2] hover:underline text-sm">Use miles</a>
                          <div className="border-t border-gray-200"></div>
                        </div>
                      </div>
                      
                      <div className="col-span-3">
                        <div className="space-y-3">
                          <a href="/i18n/aadvantage-business/overview.html" className="block text-[#0078d2] hover:underline text-sm">AAdvantage Business™ program</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="/i18n/aadvantage-program/earn-miles/offers-promotions.html" className="block text-[#0078d2] hover:underline text-sm">Offers from our partners</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="https://www.aa.com/aadvantage-program/buy-gift-transfer/en_US/home/buy" target="_blank" rel="noreferrer" className="block text-[#0078d2] hover:underline text-sm">Buy, gift or transfer miles</a>
                          <div className="border-t border-gray-200"></div>
                          <a href="/i18n/aadvantage-program/credit-cards/aadvantage-credit-cards.html" className="block text-[#0078d2] hover:underline text-sm">AAdvantage® credit cards</a>
                          <div className="border-t border-gray-200"></div>
                        </div>
                      </div>
                      
                      {/* Vertical divider */}
                      <div className="col-span-1 flex justify-center">
                        <div className="w-px bg-gray-200 h-full"></div>
                      </div>
                      
                      <div className="col-span-2">
                        <div className="space-y-2">
                          <a href="/travelInformation/flights/status" className="block text-gray-700 hover:text-[#0078d2] text-sm">Flight status</a>
                          <a href="/reservation/flightCheckInViewReservationsAccess.do" className="block text-gray-700 hover:text-[#0078d2] text-sm">Check in</a>
                          <a href="/reservation/viewReservationsAccess.do" className="block text-gray-700 hover:text-[#0078d2] text-sm">Your trips</a>
                          <a href="/loyalty/profile/summary" className="block text-gray-700 hover:text-[#0078d2] text-sm">Your account</a>
                          <a href="/AAdvantage/quickEnroll.do" className="block text-gray-700 hover:text-[#0078d2] text-sm">Join AAdvantage®</a>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='flex justify-center mb-2'>
                    <button className='bg-white rounded-md px-3 py-2 focus:border-2 focus:border-[#0860c4] focus:ring-1 focus:ring-[#0860c4]' onClick={() => setActiveDropdown(null)}>
                      <BiSolidDownArrow className='w-4 h-4 text-[#333] rotate-180' />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </nav>

          {/* Right buttons */}
          <div className="hidden md:flex items-center justify-start gap-3 w-full flex-basis: 0 flex-grow: 1 max-width: 100%; box-sizing: border-box;">
            <div className="relative">
              <button 
                className="inline-flex items-center justify-center h-9 px-4 rounded-sm bg-[#0760c4] text-white text-sm font-medium hover:bg-[#0061ab]"
              >
                Log in
              </button>
              
              {/* Login Tooltip */}
              {showLoginTooltip && (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-50 ">
                  {/* Triangle pointer */}
                  <div className="w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-[#0760c4] mx-auto"></div>
                  
                  {/* Tooltip content */}
                  <div className="bg-[#0760c4] text-white px-4 py-3 rounded-sm shadow-lg relative w-[290px] max-w-[290px]">
                    <div className="flex justify-between items-start mb-2">
                      <div className="text-lg leading-tight pr-6">
                        <div>Have an AAdvantage®</div>
                        <div>account?</div>
                        <div className="font-semibold mt-1 text-sm">Log in here.</div>
                      </div>
                      <button 
                        onClick={() => setShowLoginTooltip(false)}
                        className="w-5 h-5 rounded-full bg-white bg-opacity-20 flex items-center justify-center hover:bg-opacity-30 transition-colors flex-shrink-0"
                      >
                        <span className="text-white text-xs font-bold">×</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <a href="/loyalty/enrollment/enroll" className="inline-flex items-center justify-center h-9 px-4 rounded-sm border border-[#0078d2] text-[#0078d2] text-sm font-medium hover:bg-blue-50">Join</a>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={toggleMenu}
              className="text-gray-600 hover:text-[#0078d2] focus:outline-none"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
        <div className="border-b border-gray-200" />
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="px-4 py-3 flex items-center gap-3 border-b">
            <a href="/loyalty/login" className="inline-flex items-center justify-center h-8 px-3 rounded-sm bg-[#0078d2] text-white text-sm font-medium">Log in</a>
            <a href="/loyalty/enrollment/enroll" className="inline-flex items-center justify-center h-8 px-3 rounded-sm border border-[#0078d2] text-[#0078d2] text-sm font-medium">Join</a>
          </div>
          <div className="px-4 py-2 space-y-2">
            <a href="/reservation/roundTripSearchAccess.do" className="block text-gray-700 hover:text-[#0078d2]">Plan travel</a>
            <a href="/i18n/travel-info/at-the-airport.jsp" className="block text-gray-700 hover:text-[#0078d2]">Travel information</a>
            <a href="/i18n/aadvantage-program/overview.html" className="block text-gray-700 hover:text-[#0078d2]">AAdvantage®</a>
            <a href="/oneworld" className="block text-gray-700 hover:text-[#0078d2]">oneworld</a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
